import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Bell, BellOff, Mail, MessageSquare, Settings, CheckCheck, ArrowRightLeft, AlertTriangle, TrendingUp, Trash2, Scan, DollarSign, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [threshold, setThreshold] = useState(10);

  useEffect(() => {
    fetchAlerts();
    fetchConfig();
    const interval = setInterval(fetchAlerts, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const { data } = await api.get("/alerts?limit=50");
      setAlerts(data.data || []);
      setUnreadCount(data.unread_count || 0);
      setTotal(data.total || 0);
      setLoading(false);
    } catch (err) {
      console.error("Failed to load alerts:", err);
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const { data } = await api.get("/alerts/config");
      setConfig(data);
      setThreshold(data.threshold_pct || 10);
    } catch (err) {
      console.error("Failed to load config:", err);
    }
  };

  const updateConfig = async (updates) => {
    try {
      const { data } = await api.post("/alerts/config", updates);
      setConfig(data);
      toast.success("Alert configuration updated");
    } catch (err) {
      toast.error("Failed to update config");
    }
  };

  const markRead = async (alertId) => {
    await api.post(`/alerts/${alertId}/read`);
    fetchAlerts();
  };

  const markAllRead = async () => {
    await api.post("/alerts/read-all");
    toast.success("All alerts marked as read");
    fetchAlerts();
  };

  const triggerScan = async () => {
    setScanning(true);
    try {
      await api.post("/alerts/scan");
      toast.success("Alert scan triggered");
      setTimeout(() => { fetchAlerts(); setScanning(false); }, 3000);
    } catch (err) {
      toast.error("Scan failed");
      setScanning(false);
    }
  };

  const severityStyle = (severity) => {
    switch (severity) {
      case "critical": return { border: "border-l-2 border-l-[#ff3b30]", badge: "bg-[#ff3b30]/10 text-[#ff3b30] border-[#ff3b30]/30", icon: "text-[#ff3b30]" };
      case "high": return { border: "border-l-2 border-l-[#f97316]", badge: "bg-[#f97316]/10 text-[#f97316] border-[#f97316]/30", icon: "text-[#f97316]" };
      default: return { border: "border-l-2 border-l-[#eab308]", badge: "bg-[#eab308]/10 text-[#eab308] border-[#eab308]/30", icon: "text-[#eab308]" };
    }
  };

  return (
    <div data-testid="alerts-page" className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Arbitrage Alerts
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            {unreadCount} unread / {total} total alerts | Threshold: {config?.threshold_pct || 10}% margin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="scan-alerts-btn"
            variant="outline"
            size="sm"
            className="rounded-none border-zinc-700 text-zinc-400 hover:text-[#00ff41] text-xs uppercase tracking-wider font-mono"
            onClick={triggerScan}
            disabled={scanning}
          >
            <Scan className="w-3.5 h-3.5 mr-2" /> {scanning ? "Scanning..." : "Scan Now"}
          </Button>
          <Button
            data-testid="mark-all-read-btn"
            variant="outline"
            size="sm"
            className="rounded-none border-zinc-700 text-zinc-400 hover:text-zinc-100 text-xs uppercase tracking-wider font-mono"
            onClick={markAllRead}
          >
            <CheckCheck className="w-3.5 h-3.5 mr-2" /> Mark All Read
          </Button>
          <Button
            data-testid="toggle-config-btn"
            variant="outline"
            size="sm"
            className={`rounded-none border-zinc-700 text-xs uppercase tracking-wider font-mono ${showConfig ? "text-[#00ff41] border-[#00ff41]/30" : "text-zinc-400 hover:text-zinc-100"}`}
            onClick={() => setShowConfig(!showConfig)}
          >
            <Settings className="w-3.5 h-3.5 mr-2" /> Config
          </Button>
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && config && (
        <Card data-testid="alert-config-panel" className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <Settings className="w-3.5 h-3.5" /> Alert Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Threshold */}
              <div className="space-y-3">
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono block">
                  Margin Threshold (%)
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="threshold-input"
                    type="number"
                    min="1"
                    max="100"
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                    className="rounded-none border-zinc-800 bg-black font-mono text-sm w-24 focus:ring-1 focus:ring-[#00ff41]"
                  />
                  <Button
                    data-testid="save-threshold-btn"
                    size="sm"
                    className="rounded-none bg-[#00ff41] text-black hover:bg-[#00cc33] text-xs font-mono uppercase"
                    onClick={() => updateConfig({ threshold_pct: threshold })}
                  >
                    Save
                  </Button>
                </div>
                <p className="text-[10px] text-zinc-600 font-mono">Alert when arbitrage margin exceeds this %</p>
              </div>

              {/* SMS Config */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono flex items-center gap-2">
                    <MessageSquare className="w-3 h-3" /> SMS Alerts
                  </label>
                  <Switch
                    data-testid="sms-toggle"
                    checked={config.sms_enabled}
                    onCheckedChange={(checked) => updateConfig({ sms_enabled: checked })}
                  />
                </div>
                <Input
                  data-testid="sms-number-input"
                  value={config.sms_number || ""}
                  onChange={(e) => setConfig({ ...config, sms_number: e.target.value })}
                  onBlur={() => updateConfig({ sms_number: config.sms_number })}
                  placeholder="+255700000000"
                  className="rounded-none border-zinc-800 bg-black font-mono text-xs focus:ring-1 focus:ring-[#00ff41]"
                />
                <p className="text-[10px] text-zinc-600 font-mono">Simulated - swap-ready for M-Pesa/Twilio</p>
              </div>

              {/* Email Config */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono flex items-center gap-2">
                    <Mail className="w-3 h-3" /> Email Alerts
                  </label>
                  <Switch
                    data-testid="email-toggle"
                    checked={config.email_enabled}
                    onCheckedChange={(checked) => updateConfig({ email_enabled: checked })}
                  />
                </div>
                <Input
                  data-testid="email-input"
                  value={config.email || ""}
                  onChange={(e) => setConfig({ ...config, email: e.target.value })}
                  onBlur={() => updateConfig({ email: config.email })}
                  placeholder="operator@agridata.io"
                  className="rounded-none border-zinc-800 bg-black font-mono text-xs focus:ring-1 focus:ring-[#00ff41]"
                />
                <p className="text-[10px] text-zinc-600 font-mono">Simulated - swap-ready for SendGrid/SES</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="h-24 bg-zinc-900 animate-pulse" />)
          ) : alerts.length > 0 ? (
            alerts.map((alert) => {
              const style = severityStyle(alert.severity);
              return (
                <Card
                  key={alert.id}
                  data-testid={`alert-card-${alert.id}`}
                  className={`bg-zinc-950/50 border-zinc-800 rounded-none ${style.border} ${!alert.read ? "bg-zinc-900/30" : ""} transition-all duration-100 hover:border-zinc-600`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${alert.severity === "critical" ? "bg-[#ff3b30]/10" : alert.severity === "high" ? "bg-[#f97316]/10" : "bg-[#eab308]/10"}`}>
                          <ArrowRightLeft className={`w-4 h-4 ${style.icon}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-mono text-zinc-200">{alert.title}</h3>
                            {!alert.read && <div className="w-2 h-2 bg-[#00ff41] pulse-live flex-shrink-0" />}
                          </div>
                          <p className="text-[11px] text-zinc-400 font-mono mt-1 leading-relaxed">{alert.message}</p>
                          <div className="flex items-center gap-4 mt-2 flex-wrap">
                            <span className="text-[10px] text-zinc-500 font-mono">{new Date(alert.created_at).toLocaleString()}</span>
                            <Badge className={`rounded-none text-[8px] uppercase tracking-wider font-mono ${style.badge}`}>
                              {alert.severity}
                            </Badge>
                            {/* Delivery status */}
                            {alert.delivery?.sms?.status === "delivered" && (
                              <span className="text-[9px] text-[#00ff41] font-mono flex items-center gap-1">
                                <MessageSquare className="w-2.5 h-2.5" /> SMS sent
                              </span>
                            )}
                            {alert.delivery?.email?.status === "delivered" && (
                              <span className="text-[9px] text-[#0ea5e9] font-mono flex items-center gap-1">
                                <Mail className="w-2.5 h-2.5" /> Email sent
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {!alert.read && (
                        <Button
                          data-testid={`mark-read-${alert.id}`}
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-zinc-500 hover:text-[#00ff41] text-[10px] font-mono"
                          onClick={() => markRead(alert.id)}
                        >
                          <CheckCheck className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>

                    {/* Delivery details expandable */}
                    {alert.delivery && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {alert.delivery.sms && (
                          <div className="border border-zinc-800 p-2 bg-black/30">
                            <div className="flex items-center gap-1.5 mb-1">
                              <MessageSquare className="w-3 h-3 text-zinc-500" />
                              <span className="text-[9px] text-zinc-500 uppercase tracking-wider font-mono">SMS</span>
                              <Badge variant="outline" className={`rounded-none text-[7px] ml-auto ${alert.delivery.sms.status === "delivered" ? "border-[#00ff41]/30 text-[#00ff41]" : "border-zinc-700 text-zinc-500"}`}>
                                {alert.delivery.sms.status}
                              </Badge>
                            </div>
                            <p className="text-[9px] text-zinc-500 font-mono">To: {alert.delivery.sms.to}</p>
                            <p className="text-[9px] text-zinc-600 font-mono">Via: {alert.delivery.sms.provider}</p>
                          </div>
                        )}
                        {alert.delivery.email && (
                          <div className="border border-zinc-800 p-2 bg-black/30">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Mail className="w-3 h-3 text-zinc-500" />
                              <span className="text-[9px] text-zinc-500 uppercase tracking-wider font-mono">Email</span>
                              <Badge variant="outline" className={`rounded-none text-[7px] ml-auto ${alert.delivery.email.status === "delivered" ? "border-[#0ea5e9]/30 text-[#0ea5e9]" : "border-zinc-700 text-zinc-500"}`}>
                                {alert.delivery.email.status}
                              </Badge>
                            </div>
                            <p className="text-[9px] text-zinc-500 font-mono">To: {alert.delivery.email.to}</p>
                            <p className="text-[9px] text-zinc-600 font-mono">Via: {alert.delivery.email.provider}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
              <CardContent className="p-12 text-center">
                <BellOff className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
                <p className="text-sm text-zinc-500 font-mono">No alerts yet</p>
                <p className="text-[10px] text-zinc-600 font-mono mt-1">Click "Scan Now" to check for arbitrage opportunities exceeding your threshold</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Stats Sidebar */}
        <div className="space-y-4">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-[#00ff41]" /> Alert Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 font-mono uppercase">Unread</span>
                <span className="text-sm font-mono text-[#00ff41] font-bold">{unreadCount}</span>
              </div>
              <Separator className="bg-zinc-800" />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 font-mono uppercase">Total Alerts</span>
                <span className="text-sm font-mono text-zinc-300">{total}</span>
              </div>
              <Separator className="bg-zinc-800" />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 font-mono uppercase">Threshold</span>
                <span className="text-sm font-mono text-[#eab308]">{config?.threshold_pct || 10}%</span>
              </div>
              <Separator className="bg-zinc-800" />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 font-mono uppercase">SMS</span>
                <Badge variant="outline" className={`rounded-none text-[8px] font-mono ${config?.sms_enabled ? "border-[#00ff41]/30 text-[#00ff41]" : "border-zinc-700 text-zinc-500"}`}>
                  {config?.sms_enabled ? "ON" : "OFF"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-zinc-500 font-mono uppercase">Email</span>
                <Badge variant="outline" className={`rounded-none text-[8px] font-mono ${config?.email_enabled ? "border-[#0ea5e9]/30 text-[#0ea5e9]" : "border-zinc-700 text-zinc-500"}`}>
                  {config?.email_enabled ? "ON" : "OFF"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Impact */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none border-l-2 border-l-[#00ff41]">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5 text-[#00ff41]" /> Potential Revenue
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-mono font-bold text-[#00ff41]">
                ${alerts.reduce((sum, a) => sum + (a.net_profit || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-[10px] text-zinc-500 font-mono mt-1">
                Total arbitrage profit from {alerts.length} alerts
              </p>
              <div className="mt-3 space-y-1.5">
                {Object.entries(
                  alerts.reduce((acc, a) => {
                    const c = a.commodity || "Other";
                    acc[c] = (acc[c] || 0) + (a.net_profit || 0);
                    return acc;
                  }, {})
                ).sort(([,a], [,b]) => b - a).slice(0, 5).map(([commodity, profit]) => (
                  <div key={commodity} className="flex items-center justify-between">
                    <span className="text-[10px] text-zinc-400 font-mono">{commodity}</span>
                    <span className="text-[10px] text-[#00ff41] font-mono">${profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
